import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { logger } from "@/lib/logger";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const ENV_VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Iter103 — VAPID dynamique : tente d'abord la config DB (PUT /settings/notifications/push),
// sinon retombe sur ENV. Cache le resultat 60s.
let _cachedVapid = null;
let _cachedAt = 0;

async function _resolveVapidKey() {
  const now = Date.now();
  if (_cachedVapid && (now - _cachedAt) < 60_000) return _cachedVapid;
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    if (BACKEND_URL) {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await fetch(`${BACKEND_URL}/api/settings/notifications/push`, { headers });
      if (r.ok) {
        const data = await r.json();
        if (data?.vapid_public_key) {
          _cachedVapid = data.vapid_public_key;
          _cachedAt = now;
          return _cachedVapid;
        }
      }
    }
  } catch (e) {
    logger.info("[push] VAPID fetch from backend failed, using env fallback:", e?.message);
  }
  _cachedVapid = ENV_VAPID_KEY || "";
  _cachedAt = now;
  return _cachedVapid;
}

let app = null;
let messaging = null;
let initError = null;

try {
  if (firebaseConfig.apiKey && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
    logger.info("[push] Firebase Messaging initialized");
  } else {
    logger.info("[push] Firebase config absent — push disabled");
  }
} catch (e) {
  initError = e?.message || String(e);
  logger.warn("[push] Firebase init error:", initError);
}

/**
 * Returns a structured status object describing the push capability.
 * Useful for diagnostics in the UI.
 */
export async function getPushStatus() {
  const supported = await isSupported().catch(() => false);
  return {
    supported,
    initialized: !!messaging,
    permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
    hasConfig: !!firebaseConfig.apiKey,
    initError,
  };
}

/**
 * Request browser notification permission, register the SW, fetch a token.
 * @returns {Promise<{ok:boolean, token?:string, reason?:string}>}
 */
export async function requestNotificationPermission() {
  // 1) Browser support
  if (typeof Notification === "undefined") {
    logger.info("[push] Notifications not supported by this browser");
    return { ok: false, reason: "unsupported" };
  }
  const supported = await isSupported().catch(() => false);
  if (!supported) {
    logger.info("[push] Firebase Messaging not supported in this browser");
    return { ok: false, reason: "messaging-unsupported" };
  }
  if (!messaging || !firebaseConfig.apiKey) {
    logger.info("[push] Firebase not configured (missing REACT_APP_FIREBASE_*)");
    return { ok: false, reason: "not-configured" };
  }

  // 2) Permission prompt
  let permission = Notification.permission;
  if (permission === "default") {
    try {
      permission = await Notification.requestPermission();
    } catch (e) {
      logger.warn("[push] Permission request failed:", e);
      return { ok: false, reason: "permission-error" };
    }
  }
  if (permission !== "granted") {
    logger.info(`[push] Permission denied (${permission})`);
    return { ok: false, reason: "permission-denied", permission };
  }

  // 3) Register SW + obtain token
  try {
    const swParams = new URLSearchParams(firebaseConfig).toString();
    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${swParams}`
    );
    const vapidKey = await _resolveVapidKey();
    if (!vapidKey) {
      logger.warn("[push] No VAPID key configured (env or DB)");
      return { ok: false, reason: "no-vapid" };
    }
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      logger.warn("[push] getToken returned empty (likely VAPID/permission issue)");
      return { ok: false, reason: "no-token" };
    }
    logger.info("[push] Token obtained, length=", token.length);
    return { ok: true, token };
  } catch (e) {
    logger.warn("[push] Token request failed:", e);
    return { ok: false, reason: "token-error", error: e?.message || String(e) };
  }
}

export function onForegroundMessage(callback) {
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    logger.info("[push] foreground message:", payload?.notification?.title);
    callback(payload);
  });
}

export { messaging };
